import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getNewDashboardGraph } from "../../Redux/Actions/dashboardActions";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
} from "chart.js";
import { Line } from "react-chartjs-2";
import ChartDataLabels from "chartjs-plugin-datalabels";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  ChartDataLabels
);

const TIME_TABS = ["Day", "Months", "Year"];
const GRAPH_TABS = [
  { label: "Project", value: "projects" },
  { label: "Booking", value: "bookings" },
  { label: "Users", value: "users" },
  { label: "Builder", value: "builders" },
];
const TYPE_MAP = { Day: "week", Months: "month", Year: "year" };
const MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

const getCurrentMonthYear = () => {
  const now = new Date();
  return { month: now.getMonth() + 1, year: now.getFullYear() };
};

// Calculate how many ISO weeks overlap with a given month/year
const getWeeksInMonth = (month, year) => {
  const monthStart = new Date(year, month - 1, 1);
  const monthEnd = new Date(year, month, 0); // last day of month

  // Find the Monday of the ISO week containing monthStart
  const startDay = monthStart.getDay(); // 0=Sun,1=Mon,...
  const mondayOffset = startDay === 0 ? -6 : 1 - startDay;
  const firstMonday = new Date(monthStart);
  firstMonday.setDate(monthStart.getDate() + mondayOffset);

  let count = 0;
  let cursor = new Date(firstMonday);
  while (cursor <= monthEnd) {
    count++;
    cursor.setDate(cursor.getDate() + 7);
  }
  return count;
};

const DashboardGraph = () => {
  const dispatch = useDispatch();
  const { month: curMonth, year: curYear } = getCurrentMonthYear();

  const [activeTime, setActiveTime] = useState("Year");
  const [activeGraph, setActiveGraph] = useState("projects");
  const [selectedMonth, setSelectedMonth] = useState(curMonth);
  const [selectedYear, setSelectedYear] = useState(curYear);
  const [selectedWeek, setSelectedWeek] = useState(1);

  const {
    newDashboardGraph: dashboardGraph,
    loading: isLoading,
  } = useSelector((state) => state.dashboard || {});

  // Recalculate total weeks whenever month/year changes (for Day tab)
  const totalWeeks = useMemo(
    () => getWeeksInMonth(selectedMonth, selectedYear),
    [selectedMonth, selectedYear]
  );

  // If selectedWeek goes out of range when month changes, reset to 1
  useEffect(() => {
    if (selectedWeek > totalWeeks) setSelectedWeek(1);
  }, [totalWeeks, selectedWeek]);

  // Reset week to 1 when switching to Day tab
  useEffect(() => {
    if (activeTime === "Day") setSelectedWeek(1);
  }, [activeTime]);

  useEffect(() => {
    const payload = {
      type: TYPE_MAP[activeTime],
      graphfor: activeGraph,
      selected_month: selectedMonth,
      selected_year: selectedYear,
      ...(activeTime === "Day" && { selected_week: selectedWeek }),
    };
    dispatch(getNewDashboardGraph(payload));
  }, [activeTime, activeGraph, selectedMonth, selectedYear, selectedWeek, dispatch]);

  const labels = useMemo(
    () => dashboardGraph?.data?.result?.map((i) => i.label) || [],
    [dashboardGraph]
  );

  const dataValues = useMemo(
    () => dashboardGraph?.data?.result?.map((i) => i.total) || [],
    [dashboardGraph]
  );

  if (isLoading) {
    return (
      <div
        className="dg-wrap"
        style={{ minHeight: "400px", display: "flex", alignItems: "center", justifyContent: "center", color: "#6b7a99" }}
      >
        Loading chart data...
      </div>
    );
  }

  if (!dashboardGraph?.data?.result?.length) {
    return (
      <div
        className="dg-wrap"
        style={{ minHeight: "400px", display: "flex", alignItems: "center", justifyContent: "center", color: "#6b7a99" }}
      >
        No data available for selected filters
      </div>
    );
  }

  const chartData = {
    labels,
    datasets: [
      {
        data: dataValues,
        fill: true,
        tension: 0.4,
        borderColor: "#3DBE6E",
        borderWidth: 2,
        borderDash: [6, 4],
        backgroundColor: (ctx) => {
          const canvas = ctx.chart.ctx;
          const { chartArea } = ctx.chart;
          if (!chartArea) return "rgba(61,190,110,0.15)";
          const gradient = canvas.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
          gradient.addColorStop(0, "rgba(61,190,110,0.25)");
          gradient.addColorStop(1, "rgba(61,190,110,0.01)");
          return gradient;
        },
        pointBackgroundColor: "#1a2035",
        pointBorderColor: "#3DBE6E",
        pointBorderWidth: 2,
        pointRadius: 5,
        pointHoverRadius: 7,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      datalabels: {
        color: "#a3b0cc",
        font: {
          size: 10,  
          family: "'DM Sans', sans-serif",
        },
        offset: 2,
      },
      tooltip: {
        backgroundColor: "#1e2a45",
        titleColor: "#aab4cc",
        bodyColor: "#3DBE6E",
        borderColor: "#2e3d5c",
        borderWidth: 1,
        padding: 10,
        callbacks: { label: (ctx) => ` ${ctx.parsed.y}` },
      },
    },
    scales: {
      x: {
        grid: { color: "rgba(255,255,255,0.05)", drawBorder: false },
        ticks: { color: "#6b7a99", font: { size: 12, family: "'DM Sans', sans-serif" } },
        border: { display: false },
      },
      y: {
        grid: { color: "rgba(255,255,255,0.05)", drawBorder: false },
        ticks: {
          color: "#6b7a99",
          font: { size: 12, family: "'DM Sans', sans-serif" },
          stepSize: 1,
          callback: (v) => (Number.isInteger(v) ? v : null),
        },
        border: { display: false },
      },
    },
  };

  const years = Array.from({ length: 6 }, (_, i) => curYear - 2 + i);

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600&display=swap');
        .dg-wrap {
          font-family: 'DM Sans', sans-serif;
          background: #080d1b;
          border-radius: 20px;
          padding: 28px 32px 24px;
          width: 100%;
          box-sizing: border-box;
        }
        .dg-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 4px;
          flex-wrap: wrap;
          gap: 8px;
        }
        .dg-tabs { display: flex; gap: 4px; flex-wrap: wrap; }
        .dg-tab {
          background: none; border: none; cursor: pointer;
          font-family: 'DM Sans', sans-serif; font-size: 14px;
          font-weight: 400; color: #6b7a99;
          padding: 6px 14px; border-radius: 8px;
          position: relative; transition: all 0.2s;
        }
        .dg-tab:hover { background: rgba(255,255,255,0.06); color: #aab4cc; }
        .dg-tab.active {
          color: #fff; font-weight: 600; background: #283352;
        }
        .dg-tab.active::after {
          content: ''; position: absolute; bottom: 0;
          left: 14px; right: 14px; height: 2px;
          background: #3DBE6E; border-radius: 2px;
        }
        .dg-divider {
          height: 1px;
          background: rgba(255,255,255,0.07);
          margin: 12px 0;
        }
        .dg-filters {
          display: flex; gap: 10px;
          margin-bottom: 20px;
          align-items: center;
          flex-wrap: wrap;
        }
        .dg-select {
          font-family: 'DM Sans', sans-serif;
          font-size: 13px; color: #aab4cc;
          background: #1e2a45;
          border: 1px solid #2e3d5c;
          border-radius: 8px;
          padding: 6px 12px;
          outline: none;
          cursor: pointer;
          transition: border-color 0.2s;
        }
        .dg-select:focus { border-color: #3DBE6E; }
        .dg-chart-wrap { height: 340px; position: relative; }
      `}</style>

      <div className="dg-wrap">
        <div className="dg-header">
          <div className="dg-tabs">
            {TIME_TABS.map((t) => (
              <button
                key={t}
                className={`dg-tab${activeTime === t ? " active" : ""}`}
                onClick={() => setActiveTime(t)}
              >
                {t}
              </button>
            ))}
          </div>
          <div className="dg-tabs">
            {GRAPH_TABS.map((g) => (
              <button
                key={g.value}
                className={`dg-tab${activeGraph === g.value ? " active" : ""}`}
                onClick={() => setActiveGraph(g.value)}
              >
                {g.label}
              </button>
            ))}
          </div>
        </div>

        <div className="dg-divider" />

        <div className="dg-filters">
          {/* Month selector — hidden on Year tab */}
          {activeTime !== "Year" && (
            <select
              className="dg-select"
              value={selectedMonth}
              onChange={(e) => {
                setSelectedMonth(Number(e.target.value));
                setSelectedWeek(1); // reset week when month changes
              }}
            >
              {MONTHS.map((m, i) => (
                <option key={m} value={i + 1}>{m}</option>
              ))}
            </select>
          )}

          {/* Year selector — always visible */}
          <select
            className="dg-select"
            value={selectedYear}
            onChange={(e) => {
              setSelectedYear(Number(e.target.value));
              setSelectedWeek(1); // reset week when year changes
            }}
          >
            {years.map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>

          {/* Week selector — only on Day tab */}
          {activeTime === "Day" && (
            <select
              className="dg-select"
              value={selectedWeek}
              onChange={(e) => setSelectedWeek(Number(e.target.value))}
            >
              {Array.from({ length: totalWeeks }, (_, i) => (
                <option key={i + 1} value={i + 1}>Week {i + 1}</option>
              ))}
            </select>
          )}
        </div>

        <div className="dg-chart-wrap">
          <Line data={chartData} options={chartOptions} />
        </div>
      </div>
    </>
  );
};

export default DashboardGraph;